import { useMemo, useState } from 'react';
import { Loader2, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { notasFiscaisApi, formatarMoeda, formatarQuantidade, mensagemErro } from '@/lib/notasFiscaisApi';
import {
  CATEGORIA_PRODUTO_LABEL,
  ORIGEM_MERCADORIA,
  type CategoriaProduto,
  type ProdutoRevenda,
  type RegraFiscal,
} from '@/types/notasFiscais';

interface ProdutosRevendaTabProps {
  produtos: ProdutoRevenda[];
  regras: RegraFiscal[];
  carregando: boolean;
  onAlterado: () => void;
}

type FiltroEstoque = 'todos' | 'com_estoque' | 'zerados';
type FiltroSituacao = 'todos' | 'ativos' | 'inativos';
type FiltroCategoria = 'todas' | CategoriaProduto;

const PRODUTO_VAZIO: Partial<ProdutoRevenda> = {
  codigo: '',
  descricao: '',
  categoria: 'eletrico',
  ncm: '',
  cest: '',
  origem: 0,
  unidade: 'UN',
  preco_venda: 0,
  preco_custo: 0,
  quantidade_estoque: 0,
  regra_fiscal_id: null,
  ativo: true,
};

function passaFiltroEstoque(produto: ProdutoRevenda, filtro: FiltroEstoque): boolean {
  const quantidade = Number(produto.quantidade_estoque);
  switch (filtro) {
    case 'todos':
      return true;
    case 'com_estoque':
      return quantidade > 0;
    case 'zerados':
      return quantidade <= 0;
    default: {
      const filtroDesconhecido: never = filtro;
      return filtroDesconhecido;
    }
  }
}

function passaFiltroSituacao(produto: ProdutoRevenda, filtro: FiltroSituacao): boolean {
  switch (filtro) {
    case 'todos':
      return true;
    case 'ativos':
      return produto.ativo;
    case 'inativos':
      return !produto.ativo;
    default: {
      const filtroDesconhecido: never = filtro;
      return filtroDesconhecido;
    }
  }
}

export function ProdutosRevendaTab({ produtos, regras, carregando, onAlterado }: ProdutosRevendaTabProps) {
  const { toast } = useToast();
  const [busca, setBusca] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState<FiltroCategoria>('todas');
  const [filtroEstoque, setFiltroEstoque] = useState<FiltroEstoque>('todos');
  const [filtroSituacao, setFiltroSituacao] = useState<FiltroSituacao>('todos');
  const [editando, setEditando] = useState<Partial<ProdutoRevenda> | null>(null);
  const [salvando, setSalvando] = useState(false);

  const regraPorId = useMemo(() => new Map(regras.map((r) => [r.id, r])), [regras]);
  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return produtos.filter((p) => {
      if (filtroCategoria !== 'todas' && p.categoria !== filtroCategoria) return false;
      if (!passaFiltroEstoque(p, filtroEstoque)) return false;
      if (!passaFiltroSituacao(p, filtroSituacao)) return false;
      if (!termo) return true;
      return [p.codigo, p.descricao, p.ncm].some((v) => String(v || '').toLowerCase().includes(termo));
    });
  }, [busca, filtroCategoria, filtroEstoque, filtroSituacao, produtos]);

  const filtrosAtivos = Boolean(busca.trim()) || filtroCategoria !== 'todas' || filtroEstoque !== 'todos' || filtroSituacao !== 'todos';

  const limparFiltros = () => {
    setBusca('');
    setFiltroCategoria('todas');
    setFiltroEstoque('todos');
    setFiltroSituacao('todos');
  };

  const set = <K extends keyof ProdutoRevenda>(campo: K, valor: ProdutoRevenda[K]) =>
    setEditando((atual) => (atual ? { ...atual, [campo]: valor } : atual));

  const salvar = async () => {
    if (!editando) return;
    setSalvando(true);
    try {
      if (editando.id) await notasFiscaisApi.salvarProduto(editando.id, editando);
      else await notasFiscaisApi.criarProduto(editando);
      toast({ title: 'Produto salvo' });
      setEditando(null);
      onAlterado();
    } catch (e) {
      toast({ title: 'Erro ao salvar produto', description: mensagemErro(e), variant: 'destructive' });
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (produto: ProdutoRevenda) => {
    if (!window.confirm(`Excluir "${produto.descricao}"? Notas já emitidas não são afetadas.`)) return;
    try {
      await notasFiscaisApi.excluirProduto(produto.id);
      onAlterado();
    } catch (e) {
      toast({ title: 'Erro ao excluir', description: mensagemErro(e), variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <CardTitle>Produtos de revenda</CardTitle>
          <CardDescription>
            Materiais elétricos e de prevenção de incêndio. Copie NCM, CEST e origem da nota de compra do fornecedor.
          </CardDescription>
        </div>
        <Button onClick={() => setEditando({ ...PRODUTO_VAZIO })}><Plus className="h-4 w-4 mr-1" /> Novo produto</Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-col lg:flex-row gap-2">
          <div className="relative lg:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Buscar por código, descrição ou NCM" value={busca} onChange={(e) => setBusca(e.target.value)} />
          </div>
          <Select value={filtroCategoria} onValueChange={(v) => setFiltroCategoria(v as FiltroCategoria)}>
            <SelectTrigger className="lg:w-52"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas as categorias</SelectItem>
              {(Object.keys(CATEGORIA_PRODUTO_LABEL) as CategoriaProduto[]).map((c) => (
                <SelectItem key={c} value={c}>{CATEGORIA_PRODUTO_LABEL[c]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filtroEstoque} onValueChange={(v) => setFiltroEstoque(v as FiltroEstoque)}>
            <SelectTrigger className="lg:w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todo o estoque</SelectItem>
              <SelectItem value="com_estoque">Com estoque</SelectItem>
              <SelectItem value="zerados">Zerados</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filtroSituacao} onValueChange={(v) => setFiltroSituacao(v as FiltroSituacao)}>
            <SelectTrigger className="lg:w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="ativos">Ativos</SelectItem>
              <SelectItem value="inativos">Inativos</SelectItem>
            </SelectContent>
          </Select>
          {filtrosAtivos && (
            <Button variant="ghost" onClick={limparFiltros}>Limpar</Button>
          )}
        </div>
        {!carregando && (
          <p className="text-xs text-muted-foreground">
            {filtrados.length} de {produtos.length} produtos
          </p>
        )}
        {carregando ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : filtrados.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            {produtos.length === 0 ? 'Nenhum produto cadastrado.' : 'Nenhum produto encontrado com esses filtros.'}
          </p>
        ) : (
          <div className="border rounded-md overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>NCM</TableHead>
                  <TableHead>CEST</TableHead>
                  <TableHead>Regra fiscal</TableHead>
                  <TableHead className="text-right">Estoque</TableHead>
                  <TableHead className="text-right">Custo</TableHead>
                  <TableHead className="text-right">Venda</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtrados.map((p) => (
                  <TableRow key={p.id} className={p.ativo ? '' : 'opacity-50'}>
                    <TableCell className="font-mono text-xs">{p.codigo}</TableCell>
                    <TableCell>{p.descricao}</TableCell>
                    <TableCell><Badge variant="outline">{CATEGORIA_PRODUTO_LABEL[p.categoria]}</Badge></TableCell>
                    <TableCell>{p.ncm}</TableCell>
                    <TableCell>{p.cest || '-'}</TableCell>
                    <TableCell>{(p.regra_fiscal_id && regraPorId.get(p.regra_fiscal_id)?.nome) || 'Padrão'}</TableCell>
                    <TableCell className={`text-right ${Number(p.quantidade_estoque) <= 0 ? 'text-destructive' : ''}`}>
                      {formatarQuantidade(p.quantidade_estoque, p.unidade)}
                    </TableCell>
                    <TableCell className="text-right">{formatarMoeda(p.preco_custo)} / {p.unidade}</TableCell>
                    <TableCell className="text-right">{formatarMoeda(p.preco_venda)} / {p.unidade}</TableCell>
                    <TableCell className="whitespace-nowrap text-right">
                      <Button variant="ghost" size="icon" onClick={() => setEditando({ ...p, cest: p.cest || '' })}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => excluir(p)}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <Dialog open={Boolean(editando)} onOpenChange={(open) => !open && setEditando(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editando?.id ? 'Editar produto' : 'Novo produto'}</DialogTitle>
          </DialogHeader>
          {editando && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label>Código</Label>
                <Input value={editando.codigo || ''} onChange={(e) => set('codigo', e.target.value)} />
              </div>
              <div className="md:col-span-3 space-y-1">
                <Label>Descrição (aparece na nota)</Label>
                <Input value={editando.descricao || ''} maxLength={120} onChange={(e) => set('descricao', e.target.value)} />
              </div>
              <div className="md:col-span-2 space-y-1">
                <Label>Categoria</Label>
                <Select value={editando.categoria} onValueChange={(v) => set('categoria', v as CategoriaProduto)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(Object.keys(CATEGORIA_PRODUTO_LABEL) as CategoriaProduto[]).map((c) => (
                      <SelectItem key={c} value={c}>{CATEGORIA_PRODUTO_LABEL[c]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>NCM (8 dígitos)</Label>
                <Input value={editando.ncm || ''} onChange={(e) => set('ncm', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>CEST (se tiver ST)</Label>
                <Input value={editando.cest || ''} onChange={(e) => set('cest', e.target.value)} />
              </div>
              <div className="md:col-span-2 space-y-1">
                <Label>Origem da mercadoria</Label>
                <Select value={String(editando.origem ?? 0)} onValueChange={(v) => set('origem', Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ORIGEM_MERCADORIA.map((o) => <SelectItem key={o.valor} value={String(o.valor)}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2 space-y-1">
                <Label>Regra fiscal</Label>
                <Select
                  value={editando.regra_fiscal_id || 'padrao'}
                  onValueChange={(v) => set('regra_fiscal_id', v === 'padrao' ? null : v)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="padrao">Usar a regra padrão</SelectItem>
                    {regras.filter((r) => r.ativo).map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.nome} (CSOSN {r.csosn})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Unidade</Label>
                <Input value={editando.unidade || ''} maxLength={6} onChange={(e) => set('unidade', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Custo de aquisição</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={editando.preco_custo ?? 0}
                  onChange={(e) => set('preco_custo', Number(e.target.value))}
                />
              </div>
              <div className="space-y-1">
                <Label>Preço de venda</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={editando.preco_venda ?? 0}
                  onChange={(e) => set('preco_venda', Number(e.target.value))}
                />
              </div>
              <div className="space-y-1">
                <Label>Quantidade em estoque</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={Number(editando.quantidade_estoque ?? 0)}
                  onChange={(e) => set('quantidade_estoque', Number(e.target.value))}
                />
              </div>
              <div className="md:col-span-2 flex items-center gap-2 pt-6">
                <Switch checked={editando.ativo !== false} onCheckedChange={(v) => set('ativo', v)} />
                <span className="text-sm">Ativo</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditando(null)} disabled={salvando}>Cancelar</Button>
            <Button onClick={salvar} disabled={salvando}>
              {salvando && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
